import React, { useEffect, useRef, useState } from 'react';

// Country selector in the Admin top bar — a rounded pill with flag, name and
// arrow, matching the main website's style. Collapses to flag + code on small
// screens. Options come from the same active-countries data the rest of the
// Admin uses, so a newly activated country appears here with no code change.


// Emoji flags do not render on Windows (they show as letters), so the flags
// are drawn as small inline SVGs instead.
function FlagIcon({ code }) {
  const common = { width: 22, height: 14, viewBox: '0 0 22 14', style: { borderRadius: 2, display: 'block', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }, 'aria-hidden': 'true' };
  if (code === 'US') {
    const stripes = [];
    for (let i = 0; i < 13; i++) stripes.push(<rect key={i} x="0" y={i * (14 / 13)} width="22" height={14 / 13} fill={i % 2 === 0 ? '#B22234' : '#FFFFFF'} />);
    return (<svg {...common}>{stripes}<rect width="9.5" height="7.6" fill="#3C3B6E" /></svg>);
  }
  if (code === 'KE') {
    return (<svg {...common}><rect width="22" height="4.67" fill="#000000" /><rect y="4.67" width="22" height="0.67" fill="#FFFFFF" /><rect y="5.33" width="22" height="3.34" fill="#BB0000" /><rect y="8.67" width="22" height="0.67" fill="#FFFFFF" /><rect y="9.33" width="22" height="4.67" fill="#006600" /></svg>);
  }
  if (code === 'ALL') {
    return (<svg {...common} viewBox="0 0 14 14" width="14" height="14"><circle cx="7" cy="7" r="6" fill="none" stroke="#374151" strokeWidth="1.2" /><ellipse cx="7" cy="7" rx="2.6" ry="6" fill="none" stroke="#374151" strokeWidth="1" /><line x1="1" y1="7" x2="13" y2="7" stroke="#374151" strokeWidth="1" /></svg>);
  }
  return (<span style={{ fontSize: 10, fontWeight: 800, color: '#374151', background: '#f3f4f6', borderRadius: 3, padding: '1px 4px' }}>{code}</span>);
}

const CountryPill = ({ value, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const noActive = value === 'NONE' || options.length === 0;
  const current = noActive
    ? { code: 'NONE', label: 'No active country' }
    : (options.find((o) => o.code === value) || { code: value, label: value === 'ALL' ? 'All countries' : value });
  const displayCode = noActive ? '—' : value;
  const shownFlag = noActive ? <span aria-hidden="true">⚠️</span> : <FlagIcon code={value} />;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={wrapRef} style={s.wrap} className="country-pill-wrap">
      <style>{`
        .country-pill-code { display: none; }
        @media (max-width: 640px) {
          .country-pill-label { display: none; }
          .country-pill-name { display: none; }
          .country-pill-code { display: inline; }
        }
      `}</style>
      <span className="country-pill-label" style={s.label}>Showing for</span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Select the country you are working in"
        style={{ ...s.pill, opacity: disabled ? 0.6 : 1 }}
      >
        {shownFlag}
        <span className="country-pill-name">{current.label}</span>
        <span className="country-pill-code">{displayCode}</span>
        <span aria-hidden="true" style={s.arrow}>▾</span>
      </button>
      {open && (
        <div role="listbox" style={s.menu}>
          {options.map((o) => (
            <button
              key={o.code}
              type="button"
              role="option"
              aria-selected={o.code === value}
              onClick={() => { setOpen(false); onChange(o.code); }}
              style={{ ...s.item, background: o.code === value ? '#ecfdf5' : 'white', fontWeight: o.code === value ? 700 : 500 }}
            >
              <FlagIcon code={o.code} />
              <span>{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const s = {
  wrap: { position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 },
  label: { fontSize: 11.5, color: '#6b7280', whiteSpace: 'nowrap' },
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
    border: '1px solid #d1d5db', background: 'white', fontSize: 13, fontWeight: 600, color: '#111827', cursor: 'pointer',
  },
  arrow: { fontSize: 11, color: '#6b7280' },
  menu: {
    position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: 200, background: 'white',
    border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.12)', padding: 6, zIndex: 40,
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  item: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: 'none',
    cursor: 'pointer', fontSize: 13, color: '#111827', textAlign: 'left',
  },
};

export default CountryPill;
